import { Leaf, Flame, Wheat } from "lucide-react";
import { Reveal, MaskLines } from "./Reveal";
import { ProductionCard } from "./ProductionCard";
import { StockCard } from "./StockCard";
import { AnimatedNumber } from "./AnimatedNumber";
import { ClosuresNotice } from "./ClosuresNotice";
import { useLang } from "../../lib/i18n";

const ImpactCard = ({ totalStock, history }) => {
  const { t } = useLang();
  const weekly = history.reduce((s, h) => s + (h?.output_t ?? 0), 0);
  const items = [
    { icon: Wheat, label: t("impact.weekly"), value: weekly, unit: "t", id: "impact-weekly" },
    { icon: Flame, label: t("impact.stock"), value: totalStock, unit: "t", id: "impact-stock" },
    { icon: Leaf, label: t("impact.stubble"), value: weekly * 1.15, unit: "t", id: "impact-stubble" },
  ];
  return (
    <div data-testid="eco-impact-card" className="relative h-full overflow-hidden rounded-[28px] border hairline bg-[var(--teal)] text-white p-6 sm:p-8">
      <img src="/images/field.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity" />
      <div className="relative grid sm:grid-cols-3 gap-6 h-full">
        {items.map(({ icon: Icon, label, value, unit, id }) => (
          <div key={id} className="flex flex-col justify-between gap-6">
            <Icon className="h-5 w-5" />
            <div>
              <p className="font-display text-5xl font-bold tracking-tight">
                <AnimatedNumber value={value} decimals={0} testId={id} />
                <span className="font-serif-i font-normal text-3xl ml-1">{unit}</span>
              </p>
              <p className="mt-2 text-sm text-white/90">{label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LiveBoard = ({ plant, products, history, error, totalStock, onOrder, closures }) => {
  const { t, lang } = useLang();
  return (
  <section id="live" data-testid="live-section" className="relative py-24 sm:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-6 items-end mb-14">
        <div className="lg:col-span-8">
          <p className="eyebrow mb-5">{t("live.eyebrow")}</p>
          <h2 key={lang} className="font-display font-bold tracking-[-0.03em] leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            <MaskLines inView lines={[t("live.h1"), <span key="s">{t("live.pre")}<span className="font-serif-i font-normal text-[var(--husk)]">{t("live.accent")}</span>{t("live.post")}</span>]} delay={0} />
          </h2>
        </div>
        <p className="lg:col-span-4 text-[var(--sage)] text-base leading-relaxed">
          {t("live.sub")}
        </p>
      </div>
      {error && !plant && <p data-testid="live-error" className="mb-6 text-amber-300">{t("live.error")}</p>}
      {closures?.length > 0 && <div className="mb-6"><ClosuresNotice closures={closures} /></div>}
      <div className="grid grid-cols-12 gap-5 lg:gap-6 lg:auto-rows-[minmax(250px,auto)]">
        <Reveal className="col-span-12 lg:col-span-8 lg:row-span-2"><ProductionCard plant={plant} history={history} /></Reveal>
        {products.slice(0, 2).map((p, i) => (
          <Reveal key={p.slug} delay={0.1 + i * 0.1} className="col-span-12 md:col-span-6 lg:col-span-4"><StockCard product={p} onOrder={onOrder} /></Reveal>
        ))}
        <Reveal className="col-span-12 lg:col-span-8 lg:order-none order-last"><ImpactCard totalStock={totalStock} history={history} /></Reveal>
        {products.slice(2).map((p) => (
          <Reveal key={p.slug} delay={0.1} className="col-span-12 md:col-span-6 lg:col-span-4"><StockCard product={p} onOrder={onOrder} /></Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};
